import type { InsightsSavedView, SavedViewFilters } from '#services/api/saved-views.ts';

import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog } from '@mend/mui';
import { Button, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { savedViews } from '#lib/react-query/queries.ts';
import { useFilters } from '#routes/Home/Filters/FiltersContext.tsx';
import { createSavedView, updateSavedView } from '#services/api/saved-views.ts';

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(244, 'Name is too long'),
});

interface SavedViewDialogBaseProps {
  onClose: () => void;
}

interface SavedViewDialogCreateProps extends SavedViewDialogBaseProps {
  view?: never;
  tabType: InsightsSavedView['tabType'];
  getFilters: () => SavedViewFilters;
  onSave: (viewId: number) => void;
}

interface SavedViewDialogRenameProps extends SavedViewDialogBaseProps {
  view: InsightsSavedView;
  tabType?: never;
  getFilters?: never;
  onSave?: never;
}

type SavedViewDialogProps = SavedViewDialogCreateProps | SavedViewDialogRenameProps;

export function SavedViewDialog(props: SavedViewDialogProps) {
  const titleId = React.useId();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const filters = useFilters();

  const createMutation = useMutation({
    mutationFn: createSavedView,
    onSuccess: async (data) => {
      enqueueSnackbar('View saved', { variant: 'success' });
      await queryClient.invalidateQueries({ queryKey: savedViews.queryKey });
      filters.selectView(data.view.id);
      props.onSave?.(data.view.id);
    },
    onError: () => {
      enqueueSnackbar('Failed to save view', { variant: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (params: { id: number; name: string }) =>
      updateSavedView(params.id, { name: params.name }),
    onSuccess: () => {
      enqueueSnackbar('View renamed', { variant: 'success' });
      props.onClose();
      void queryClient.invalidateQueries({ queryKey: savedViews.queryKey });
    },
    onError: () => {
      enqueueSnackbar('Failed to rename view', { variant: 'error' });
    },
  });

  const mutation = props.view ? updateMutation : createMutation;

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { name: props.view?.name ?? '' },
  });

  const onSubmit = form.handleSubmit((data) => {
    if (props.view) {
      updateMutation.mutate({ id: props.view.id, name: data.name });
    } else {
      createMutation.mutate({
        tabType: props.tabType,
        name: data.name,
        filters: props.getFilters(),
      });
    }
  });

  function handleClose() {
    form.reset();
    props.onClose();
  }

  return (
    <Dialog.Root open onClose={handleClose} maxWidth="xs" fullWidth aria-labelledby={titleId}>
      {/* oxlint-disable-next-line typescript/no-misused-promises */}
      <form onSubmit={onSubmit}>
        <Dialog.Header>
          <Dialog.Title id={titleId}>{props.view ? 'Rename' : 'Save Current'} View</Dialog.Title>
        </Dialog.Header>

        <Dialog.Content>
          <TextField
            {...form.register('name')}
            autoFocus
            label="View Name"
            placeholder="e.g. AOP Q2"
            error={!!form.formState.errors.name}
            helperText={form.formState.errors.name?.message}
            fullWidth
            disabled={mutation.isPending}
          />
        </Dialog.Content>

        <Dialog.Footer>
          <Dialog.DismissButton />

          <Button
            type="submit"
            variant="contained"
            color="secondary"
            size="large"
            disabled={mutation.isPending}
          >
            {props.view ? 'Rename' : 'Save'} View
          </Button>
        </Dialog.Footer>
      </form>
    </Dialog.Root>
  );
}
